import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getMissingCostBasis, MissingCostBasisItem, MissingCostBasisParams } from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatUsd, formatCrypto, formatDate, formatOrderId } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';
import InboxResolutionFlow from './InboxResolutionFlow';

// Server-paginated. See CoinbaseTransactionsView.tsx for the canonical idiom.

/**
 * MissingCostBasisRowAction
 *
 * Per-row "Resolve" trigger. Renders a small pill-style button that, when
 * clicked, invokes the supplied `onResolve` callback so the parent view can
 * mount the InboxResolutionFlow for the chosen item. Kept as its own
 * component so the table cell stays declarative and testable.
 */
export const MissingCostBasisRowAction: React.FC<{
  item: MissingCostBasisItem;
  onResolve: (item: MissingCostBasisItem) => void;
}> = ({ item, onResolve }) => {
  return (
    <button
      type="button"
      className="modal-pill-button modal-pill-primary"
      onClick={() => onResolve(item)}
      aria-label={`Resolve missing cost basis item ${item.result_id}`}
    >
      Resolve
    </button>
  );
};

const itemTypeBadge = (v: MissingCostBasisItem['item_type']) => {
  if (v === 'UNASSIGNED_DISPOSAL') {
    return <span className="badge badge-failed">UNASSIGNED</span>;
  }
  return <span className="badge badge-open">ZERO COST</span>;
};

export const MissingCostBasisView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');
  const queryClient = useQueryClient();

  const [portfolioId, setPortfolioId] = useState('');
  const [itemType, setItemType] = useState<'' | MissingCostBasisItem['item_type']>('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'occurrence_date', desc: true }]);
  const [selectedItem, setSelectedItem] = useState<MissingCostBasisItem | null>(null);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;


  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const params: MissingCostBasisParams = {
    taxYear,
    portfolioId: portfolioId || undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax', 'missing-cost-basis', taxYear, portfolioId, sortBy, sortDir, pageIndex, pageSize, refreshTrigger],
    queryFn: () => getMissingCostBasis(params),
  });

  const allItems = data?.items ?? [];
  // item_type filtering is done client-side over the current page (not a server filter today).
  const items = itemType ? allItems.filter((i) => i.item_type === itemType) : allItems;
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const { options: portfolioOptions } = usePortfolioRegistry();

  const columns = useMemo<ColumnDef<MissingCostBasisItem>[]>(
    () => [
      {
        accessorKey: 'item_type',
        header: 'Type',
        cell: (info) => itemTypeBadge(info.getValue() as MissingCostBasisItem['item_type']),
      },
      {
        accessorKey: 'occurrence_date',
        header: 'Date',
        cell: (info) => <span className="date-cell">{formatDate(info.getValue() as string)}</span>,
      },
      { accessorKey: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', header: 'Product' },
      { accessorKey: 'quantity', header: 'Quantity', cell: (info) => formatCrypto(info.getValue() as string) },
      {
        accessorKey: 'usd_value',
        header: 'USD Value',
        cell: (info) => formatUsd(info.getValue() as string | null),
      },
      { accessorKey: 'source_type', header: 'Source' },
      {
        accessorKey: 'source_order_id',
        header: 'Source Order',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
      {
        id: 'action',
        header: '',
        cell: (info) => (
          <MissingCostBasisRowAction
            item={info.row.original}
            onResolve={setSelectedItem}
          />
        ),
        enableSorting: false,
      },
    ],
    []
  );

  const filters: DataTableFilter[] = [
    {
      id: 'item_type',
      label: 'Type',
      value: itemType,
      options: [
        { value: 'UNASSIGNED_DISPOSAL', label: 'Unassigned Disposal' },
        { value: 'ZERO_COST_ACQUISITION', label: 'Zero-Cost Acquisition' },
      ],
      onChange: (v) => {
        setItemType(v as '' | MissingCostBasisItem['item_type']);
        setPageIndex(0);
      },
    },
    { id: 'portfolio_id', label: 'Portfolio', value: portfolioId, options: portfolioOptions, onChange: onFilterChange(setPortfolioId) },
  ];

  return (
    <div aria-label="Missing Cost Basis view">
      <DataTable<MissingCostBasisItem>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        loadingMessage="Loading missing cost basis…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load missing cost basis items'}
        emptyMessage="No missing cost basis items for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
      {selectedItem && (
        <InboxResolutionFlow
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onSuccess={() => {
            setSelectedItem(null);
            queryClient.invalidateQueries({ queryKey: ['tax', 'missing-cost-basis'] });
          }}
        />
      )}
    </div>
  );
};

export default MissingCostBasisView;
